import { alpha, Theme } from '@mui/material';
import { useBlendColors } from 'hooks/useDesign';

export default {
    dialogItem: {
        px: 1.5,
        pt: 1.5,
        pb: 4,
        '&.userItem': {
            backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.04),
        },
        '&.botItem': {},
        '&.botItemSummary': {
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: (theme: Theme) => useBlendColors(alpha(theme.palette.primary.main, 0.08)),
            },
        },
        borderBottom: (theme: Theme) => `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
    },
    dialogItemAvatar: {
        width: '24px',
        height: '24px',
        mr: 2,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        display: 'inline-flex',
        color: 'primary.main',
    },
    dialogItemAvatarUser: {
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        display: 'inline-flex',
        borderRadius: '50%',
        overflow: 'hidden',
        img: {
            maxWidth: '100%',
            maxHeight: '100%',
        },
    },
    badgeHolder: {
        position: 'absolute',
        top: '25px',
        left: '30px',
        height: '20px',
        width: '20px',
        padding: '2px',
        backgroundColor: (theme: Theme) => theme.palette.background.default,
        borderRadius: '50%',
        '.itemSelected &': {
            backgroundColor: (theme: Theme) => useBlendColors(alpha(theme.palette.text.primary, 0.08)),
        },
        '.botItemSummary:hover &': {
            backgroundColor: (theme: Theme) => useBlendColors(alpha(theme.palette.primary.main, 0.08)),
        },
    },
    dialogItemText: {
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        overflow: 'hidden',
        '& p': {
            marginBlockStart: '0',
        },
        '.read-more-state': {
            display: 'none',
        },
        '.read-more-wrap .read-more-target': {
            display: 'none',
        },
        '.see-more-block': {
            padding: '10px',
            marginBottom: '20px',
            backgroundColor: '#ebf2f5',
            borderRadius: '4px',
            width: '100%',
            display: 'block',
            textAlign: 'center',

            '&__button': {
                display: 'inline-block',
                marginLeft: '10px',
                padding: '7px 10px',
                position: 'relative',
                background: '#37809f',
                borderRadius: '3px',
                color: '#fff',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '14px',
                height: '28px',
                outline: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'none',

                '&:hover': {
                    background: '#37809f',
                },
            },
        },
        messageText: {
            flex: '1',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            overflow: 'hidden',
            maxWidth: '100%',
            '& p': {
                marginBlockStart: '0',
                marginBlockEnd: '0.75em',
            },
            '& a': {
                textDecoration: 'underline',
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
                marginTop: '1em',
                marginBottom: '0.5em',
            },
            h1: {
                fontSize: '12px',
            },
            ol: {
                listStyle: 'decimal !important',
                listStyleType: 'decimal !important',
                li: {
                    listStyle: 'decimal !important',
                    listStyleType: 'decimal !important',
                },
                paddingLeft: 'revert !important',
                marginBottom: '10px',
            },
            ul: {
                paddingLeft: 'revert !important',
                marginBottom: '10px',
                listStyle: 'circle !important',
                listStyleType: 'circle !important',
                li: {
                    listStyle: 'circle !important',
                    listStyleType: 'circle !important',
                },
            },
        },
    },
    formulaContainer: {
        overflowX: 'auto',
        padding: '10px',
        background: 'rgba(38, 10, 37, 0.08)',
        borderRadius: '2px',
        width: 'calc(100% - 10px)',
        margin: '0 auto 10px',
        maxWidth: 'calc(100% - 10px)',

        '.katex': {
            fontSize: '1.1em',
        },
    },
    params: {
        color: (theme: Theme) => alpha(theme.palette.text.primary, 0.64),
        borderTop: (theme: Theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        paddingTop: '10px',

        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '18px',
        textUnderlinePosition: 'from-font',
        textDecorationSkipInk: 'none',

        marginBottom: '20px',
    },
    showInSearch: {
        backgroundColor: (theme: Theme) => useBlendColors(alpha(theme.palette.primary.main, 0.04)),
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: (theme: Theme) => useBlendColors(alpha(theme.palette.primary.main, 0.24)),
        pt: 0.5,
        pb: 0.5,
        borderRadius: '3px',
        color: (theme: Theme) => theme.palette.primary.main,
        alignItems: 'center',
        justifyContent: 'center',
        height: '26px',
        '&:hover': {
            cursor: 'pointer',
            borderColor: (theme: Theme) => useBlendColors(alpha(theme.palette.primary.main, 0.6)),
            backgroundColor: (theme: Theme) => useBlendColors(alpha(theme.palette.primary.main, 0.08)),
        },
        '&.loading': {
            cursor: 'progress',
            backgroundColor: (theme: Theme) => useBlendColors(alpha(theme.palette.text.primary, 0.04)),
            borderColor: (theme: Theme) => useBlendColors(alpha(theme.palette.text.primary, 0.24)),
            color: (theme: Theme) => useBlendColors(alpha(theme.palette.primary.main, 0.4)),
        },
    },
    showInSearchIcon: {
        pt: 0.5,
    },
    searchParamsIcon: {
        position: 'absolute',
        right: '25px',
        bottom: '14px',
        color: (theme: Theme) => alpha(theme.palette.text.primary, 0.65),
    },
    tool: {
        border: '1px solid',
        borderColor: (theme: Theme) => theme.palette.primary.main,
        borderRadius: '3px',
        padding: '4px 8px',
        display: 'inline-flex',
        alignItems: 'center',
        marginRight: '6px',
        cursor: 'pointer',
        gap: '4px',
        backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.04),
        color: (theme: Theme) => theme.palette.primary.main,
        '&:hover': {
            backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
        },
        '&.selected': {
            backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.15),
        },
    },
};
